import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import axios from "../../../api/axios";
import { MagnifyingGlassIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { useAutofocusOnOpen } from "../useAutofocusOnOpen";

export const DrawingSelector = ({ 
  projectId = null, 
  selected = null, 
  onChange, 
  placeholder = "Velg tegning...",
  disabled = false 
}) => {
  const { t } = useTranslation();
  const [drawings, setDrawings] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const searchInputRef = useAutofocusOnOpen(isOpen);

  useEffect(() => {
    if (isOpen) {
      loadDrawings();
    }
  }, [isOpen, projectId]);

  const loadDrawings = async () => {
    setIsLoading(true);
    try {
      const params = {
        query: searchQuery,
        per_page: 50,
        page: 1,
        fields: "id,title,description,document.file",
      };
      
      if (projectId) {
        params.project_id = projectId;
      }

      const response = await axios({
        method: "get",
        url: "/drawings/",
        params: params,
      });
      
      setDrawings(response.data.data || []);
    } catch (error) {
      console.error("Error loading drawings:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen && searchQuery) {
      const timer = setTimeout(() => {
        loadDrawings();
      }, 300);
      return () => clearTimeout(timer);
    } else if (isOpen) {
      loadDrawings();
    }
  }, [searchQuery, isOpen]);

  const handleSelect = (drawing) => {
    onChange(drawing);
    setIsOpen(false);
    setSearchQuery("");
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange(null);
  };

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className="w-full px-3 py-2 text-left border border-gray-300 rounded-md bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-between"
      >
        <span className={selected ? "text-gray-900" : "text-gray-500"}>
          {selected ? selected.title : placeholder}
        </span>
        {selected && !disabled && (
          <button
            type="button"
            onClick={handleClear}
            className="ml-2 text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon className="h-4 w-4" />
          </button>
        )}
      </button>

      {isOpen && (
        <>
          <div
            className="fixed inset-0 z-10"
            onClick={() => setIsOpen(false)}
          />
          <div className="absolute z-20 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
            {/* Search */}
            <div className="p-2 border-b border-gray-200">
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  ref={searchInputRef}
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Søk etter tegninger..."
                  className="w-full pl-8 pr-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            {/* Results */}
            {isLoading ? (
              <div className="p-4 text-center text-gray-500 text-sm">
                Laster...
              </div>
            ) : drawings.length === 0 ? (
              <div className="p-4 text-center text-gray-500 text-sm">
                Ingen tegninger funnet
              </div>
            ) : (
              <div className="py-1">
                {drawings.map((drawing) => (
                  <button
                    key={drawing.id}
                    type="button"
                    onClick={() => handleSelect(drawing)}
                    className="w-full px-3 py-2 text-left hover:bg-blue-50 focus:bg-blue-50 focus:outline-none"
                  >
                    <div className="text-sm font-medium text-gray-900">
                      {drawing.title}
                    </div>
                    {drawing.description && (
                      <div className="text-xs text-gray-500 truncate">
                        {drawing.description}
                      </div>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

